import { useEffect, useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowDown01Icon } from '@hugeicons/core-free-icons';
import { BULK_LIMIT_NOTICE, BULK_SELECT_FIRST_LABEL } from '@overlayqa/shared';
import type { BulkAction, SubMenuItem } from './DataTableTypes';

export interface BulkActionBarProps<T> {
  selectedCount: number;
  actions: BulkAction<T>[];
  selectedIds: string[];
  loading?: boolean;
  /** Selection exceeds the per-request cap: actions are blocked until trimmed. */
  overSelectionLimit?: boolean;
  onSelectFirstAllowed?: () => void;
}

/** "You can act on up to 100 issues at a time. Select first 100" (both bar placements). */
export function BulkSelectionLimitNotice({ onSelectFirstAllowed }: { onSelectFirstAllowed?: () => void }) {
  return (
    <span role="status" className="text-sm text-gray-700">
      {BULK_LIMIT_NOTICE}{' '}
      <button type="button" className="font-semibold text-primary-700 underline hover:text-primary-800" onClick={onSelectFirstAllowed}>
        <span className="text-cap-trim block">{BULK_SELECT_FIRST_LABEL}</span>
      </button>
    </span>
  );
}

export function BulkActionBar<T>({ selectedCount, actions, selectedIds, loading = false, overSelectionLimit = false, onSelectFirstAllowed }: BulkActionBarProps<T>) {
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  // Escape / outside-press dismissal (TicketActions.tsx pattern). Load-bearing:
  // the Change Status submenu floats mutation buttons over the table while N
  // rows are selected, so a menu that only closes on re-toggle or item pick
  // turns any stray click into a bulk mutation of the whole selection.
  useEffect(() => {
    if (!openSubmenu) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        setOpenSubmenu(null);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenSubmenu(null);
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openSubmenu]);

  const handleAction = async (action: BulkAction<T>) => {
    const submenuItems = action.getSubmenu?.(selectedIds) ?? action.submenu;
    if (submenuItems) {
      setOpenSubmenu(openSubmenu === action.label ? null : action.label);
      return;
    }
    setOpenSubmenu(null);
    await action.onClick(selectedIds);
  };

  const handleSubmenuClick = (subItem: SubMenuItem) => {
    if (subItem.disabled) return;
    subItem.onClick();
    setOpenSubmenu(null);
  };

  return (
    <div
      ref={barRef}
      className="bg-primary-50 border border-primary-200 rounded-lg px-4 py-3 mb-3 flex items-center gap-3"
      role="toolbar"
      aria-label={`${selectedCount} items selected`}
    >
      <span className="text-sm font-medium text-primary-700">
        {selectedCount} selected
      </span>
      {overSelectionLimit && <BulkSelectionLimitNotice onSelectFirstAllowed={onSelectFirstAllowed} />}

      <div className="flex items-center gap-2 ml-auto">
        {actions.map((action) => {
          const submenuItems = action.getSubmenu?.(selectedIds) ?? action.submenu;
          const hasSubmenu = !!submenuItems;
          const actionDisabled = action.isDisabled?.(selectedIds) ?? false;
          const buttonDisabled = loading || actionDisabled || overSelectionLimit;

          return (
            <div key={action.label} className="relative">
              <button
                className={`text-sm px-3 py-2 rounded-md font-medium ${
                  action.destructive
                    ? 'text-red-600 hover:bg-red-50 border border-red-200'
                    : 'btn-secondary'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                onClick={() => handleAction(action)}
                disabled={buttonDisabled}
                title={actionDisabled ? action.title : undefined}
              >
                <span className="text-cap-trim">{action.label}</span>
                {hasSubmenu && (
                  <HugeiconsIcon icon={ArrowDown01Icon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5 ml-1 inline" aria-hidden="true" />
                )}
              </button>

              {submenuItems && openSubmenu === action.label && (
                <div className="absolute top-full mt-1 left-0 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[160px]">
                  {submenuItems.map((subItem) => (
                    <button
                      key={subItem.label}
                      className={`w-full text-left px-3 py-2 text-sm whitespace-nowrap ${
                        subItem.disabled
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => handleSubmenuClick(subItem)}
                      disabled={subItem.disabled}
                      title={subItem.disabled ? subItem.title : undefined}
                    >
                      <span className="text-cap-trim block">{subItem.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
